import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const stats = [
  {
    number: 500,
    suffix: "+",
    label: "Produkte",
    sub: "verëra, birra & pije joalkoolike",
    index: "01",
  },
  {
    number: 15,
    suffix: "+",
    label: "Marka",
    sub: "nga e gjithë bota",
    index: "02",
  },
  {
    number: 5,
    suffix: "K+",
    label: "Klientë",
    sub: "që na vizitojnë çdo vit",
    index: "03",
  },
  {
    number: 1,
    suffix: "",
    label: "Showroom",
    sub: "Te paret ne vend per cilesi",
    index: "04",
  },
];

function useCountUp(target, duration = 2000, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return count;
}

const StatCard = ({ stat, active }) => {
  const count = useCountUp(stat.number, 1800, active);
  return (
    <Card>
      <CardIndex>{stat.index}</CardIndex>
      <CardGlow />
      <CardInner>
        <NumberRow>
          <BigNumber>{count}</BigNumber>
          <Suffix>{stat.suffix}</Suffix>
        </NumberRow>
        <CardLabel>{stat.label}</CardLabel>
        <CardSub>{stat.sub}</CardSub>
      </CardInner>
      <CardLine />
    </Card>
  );
};

const StatsSection = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const gridRef = useRef(null);
  const marqueeRef = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    const heading = headingRef.current;
    const grid = gridRef.current;

    // Heading reveal
    gsap.fromTo(
      heading,
      { opacity: 0, y: 60 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: section,
          start: "top 75%",
        },
      },
    );

    // Cards stagger in
    gsap.fromTo(
      grid.children,
      { opacity: 0, y: 80, scale: 0.92 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.15,
        scrollTrigger: {
          trigger: grid,
          start: "top 80%",
          onEnter: () => setActive(true),
        },
      },
    );

    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []);

  return (
    <Section ref={sectionRef} id="stats-section">
      {/* Background texture lines */}
      <BgLines>
        {[...Array(8)].map((_, i) => (
          <BgLine key={i} style={{ left: `${i * 14.5}%` }} />
        ))}
      </BgLines>

      <Inner>
        <HeadingBlock ref={headingRef}>
          <EyebrowRow>
            <EyebrowLine />
            <Eyebrow>Në Numra</Eyebrow>
            <EyebrowLine />
          </EyebrowRow>
          <Heading>
            Çdo shifrë <AccentWord>tregon</AccentWord>
            <br />
            një histori.
          </Heading>
          <HeadingSub>
            Një showroom i krijuar për dashamirët e pijeve — <br />
            nga verërat më të mira deri tek birrat artizanale.
          </HeadingSub>
        </HeadingBlock>

        <Grid ref={gridRef}>
          {stats.map((s) => (
            <StatCard key={s.index} stat={s} active={active} />
          ))}
        </Grid>

        {/* Scrolling marquee */}
        <MarqueeWrapper>
          <MarqueeTrack ref={marqueeRef}>
            {[...Array(3)].map((_, i) => (
              <MarqueeInner key={i}>
                {[
                  "SHIJE",
                  "✦",
                  "CILËSI",
                  "✦",
                  "TRADITË",
                  "✦",
                  "PASION",
                  "✦",
                ].map((word, j) => (
                  <MarqueeItem key={j} $accent={word === "✦"}>
                    {word}
                  </MarqueeItem>
                ))}
              </MarqueeInner>
            ))}
          </MarqueeTrack>
        </MarqueeWrapper>
      </Inner>
    </Section>
  );
};

export default StatsSection;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const marqueeScroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-33.333%); }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(1); }
  50%       { opacity: 0.35; transform: scale(1.15); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Section = styled.section`
  position: relative;
  width: 100vw;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 8rem 0 4rem;
  background: #140e0e;
  z-index: 999;
`;

const BgLines = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const BgLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(139, 58, 26, 0.08) 30%,
    rgba(139, 58, 26, 0.08) 70%,
    transparent
  );
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 0 4rem;
  display: flex;
  flex-direction: column;
  gap: 5rem;
`;

const HeadingBlock = styled.div`
  opacity: 0;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const EyebrowLine = styled.div`
  flex: 0 0 40px;
  height: 1px;
  background: var(--accent);
  opacity: 0.6;
`;

const Eyebrow = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--accent);
`;

const Heading = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(3.5rem, 7vw, 6.5rem);
  line-height: 0.92;
  color: var(--white);
  letter-spacing: -0.01em;
  margin-bottom: 1.5rem;
`;

const AccentWord = styled.span`
  color: var(--accent);
  font-style: italic;
`;

const HeadingSub = styled.p`
  font-family: var(--font-body);
  font-size: 0.95rem;
  font-weight: 300;
  line-height: 1.8;
  color: var(--white-dim);
  max-width: 460px;
`;

/* ── Grid ── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: rgba(139, 58, 26, 0.12);
  border: 1px solid rgba(139, 58, 26, 0.12);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.div`
  position: relative;
  background: var(--bg);
  padding: 2.8rem 2rem 2.4rem;
  overflow: hidden;
  cursor: default;
  transition: background 0.4s ease;

  &:hover {
    background: rgba(139, 58, 26, 0.06);
  }

  &:hover > div:nth-child(2) {
    animation-play-state: running;
  }
`;

const CardIndex = styled.span`
  position: absolute;
  top: 1.4rem;
  right: 1.6rem;
  font-family: var(--font-condensed);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: rgba(139, 58, 26, 0.4);
`;

const CardGlow = styled.div`
  position: absolute;
  top: -40px;
  left: -40px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(139, 58, 26, 0.25), transparent 70%);
  animation: ${pulseGlow} 3s ease-in-out infinite;
  animation-play-state: paused;
  pointer-events: none;
`;

const CardInner = styled.div`
  position: relative;
  z-index: 1;
`;

const NumberRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  margin-bottom: 0.6rem;
  line-height: 1;
`;

const BigNumber = styled.span`
  font-family: var(--font-display);
  font-size: clamp(3.5rem, 5vw, 5rem);
  color: var(--white);
  line-height: 1;
  letter-spacing: -0.02em;
`;

const Suffix = styled.span`
  font-family: var(--font-display);
  font-size: clamp(2rem, 3vw, 2.8rem);
  color: var(--accent);
  line-height: 1.2;
  margin-bottom: 0.1rem;
`;

const CardLabel = styled.p`
  font-family: var(--font-condensed);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--white);
  margin-bottom: 0.4rem;
`;

const CardSub = styled.p`
  font-family: var(--font-body);
  font-size: 0.78rem;
  font-weight: 300;
  color: var(--white-dim);
  line-height: 1.5;
`;

const CardLine = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(to right, var(--accent), transparent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.5s ease;

  ${Card}:hover & {
    transform: scaleX(1);
  }
`;

/* ── Marquee ── */
const MarqueeWrapper = styled.div`
  overflow: hidden;
  border-top: 1px solid rgba(139, 58, 26, 0.15);
  border-bottom: 1px solid rgba(139, 58, 26, 0.15);
  padding: 1.2rem 0;
  margin: 0 -4rem;
`;

const MarqueeTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${marqueeScroll} 18s linear infinite;
`;

const MarqueeInner = styled.div`
  display: flex;
  align-items: center;
`;

const MarqueeItem = styled.span`
  font-family: var(--font-display);
  font-size: 1rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: ${({ $accent }) =>
    $accent ? "var(--accent)" : "rgba(240,236,230,0.2)"};
  padding: 0 1.5rem;
  white-space: nowrap;
`;
